import './PitchLocationChart.scss';

import { PitchLocationChartDataset, PitchLocationChartPoint } from 'types/CodegenReportTypes';

import StrikeZone, {
  BasePlateOrientation,
  generateRandomStrikeZonePoints,
  getRelativeStrikeZonePointX,
  getRelativeStrikeZonePointY,
} from 'components/Charts/StrikeZone/StrikeZone';
import { memo, useCallback, useMemo } from 'react';
import { mergeFoulBallVariants, meterToFeet } from 'utilities/constants';
import { useChartDataDisplay, useGetUnitSystem } from 'hooks/helperHooks';
import Legend, { LegendItem } from 'components/Charts/Legend/Legend';
import { Tooltip, Typography } from '@trackman/web-shared-components';
import { useChartBrokerStore } from 'stores/ChartBrokerStore';
import { UnitSystem } from 'types/StartupTypes';

interface PitchLocationChartProps {
  chartData: PitchLocationChartDataset[] | undefined;
  basePlateOrientation: BasePlateOrientation;
  showBufferZone?: boolean;
  showStrikeZoneGrid?: boolean;
  groupLabelsToSimulate: string[];
  getGroupLabelColor: (groupLabel: string) => string;
  getGroupLabelTextColor: (groupLabel: string) => string;
  extraLegendItems?: LegendItem[];
  enablePitchSelection?: boolean;
  isLoading: boolean;
}

type PitchPointProps = {
  point: PitchLocationChartPoint;
  color: string;
  textColor: string;
  isSelected: boolean;
  onSelect?: (pitchOfGame: number) => void;
  orientation: 'PitcherSide' | 'BatterSide';
  unitSystem: UnitSystem;
};

const PitchPoint = memo(({ point, color, textColor, isSelected, onSelect, orientation, unitSystem }: PitchPointProps) => {
  const positionX = getRelativeStrikeZonePointX(
    point.x * (unitSystem === 'Metric' ? meterToFeet : 1) * (orientation === 'PitcherSide' ? 1 : -1)
  );
  const positionY = getRelativeStrikeZonePointY(point.y * (unitSystem === 'Metric' ? meterToFeet : 1));

  return (
    <Tooltip title={`#${point.pitchOfGame} (${point.x.toFixed(2)}, ${point.y.toFixed(2)})`}>
      <span
        className={`StrikeZone-point ${isSelected ? 'selected' : ''}`}
        style={{
          left: `${positionX}%`,
          bottom: `${positionY}%`,
          backgroundColor: color + '80',
          borderColor: color,
          display: 'grid',
          placeItems: 'center',
          padding: 1,

          cursor: onSelect ? 'pointer' : 'default',
          outline: isSelected ? '2px solid #EC691A' : 'none',
          outlineOffset: '2px',
          zIndex: isSelected ? 1 : 0,
        }}
        onClick={(e) => {
          e.stopPropagation();
          onSelect?.(point.pitchOfGame);
        }}
      >
        <Typography variant={'label'} style={{ color: textColor, lineHeight: 0 }}>
          {point.pitchOfGame}
        </Typography>
      </span>
    </Tooltip>
  );
});

const PitchLocationChart = ({
  chartData,
  basePlateOrientation,
  showBufferZone,
  showStrikeZoneGrid,
  groupLabelsToSimulate,
  getGroupLabelColor,
  getGroupLabelTextColor,
  extraLegendItems,
  enablePitchSelection,
  isLoading,
}: PitchLocationChartProps) => {
  const simulateChartData = useCallback(
    (isLoading: boolean): PitchLocationChartDataset[] => {
      return isLoading
        ? groupLabelsToSimulate
            .filter((label) => label !== 'Undefined')
            .map((label, i) => ({
              label: label,
              points: generateRandomStrikeZonePoints(Math.round(50 / groupLabelsToSimulate.length)).map(({ x, y }, j) => ({
                x,
                y,
                pitchOfGame: i * 4 + j + 1,
              })),
            }))
        : [];
    },
    [groupLabelsToSimulate]
  );

  const chartDataDisplay = useChartDataDisplay(chartData, simulateChartData, isLoading);

  const unitSystem = useMemo(() => (isLoading ? 'Imperial' : useGetUnitSystem()), [isLoading]);

  const groupLabels = useMemo(() => [...new Set(chartDataDisplay.map((group) => mergeFoulBallVariants(group.label)))], [chartDataDisplay]);

  const legendItems: LegendItem[] = useMemo(
    () => [
      ...groupLabels.map((groupLabel) => ({
        label: groupLabel,
        dotProps: { color: getGroupLabelColor(groupLabel) },
      })),
      ...(extraLegendItems ?? []),
    ],
    [groupLabels, extraLegendItems]
  );

  const { selectedPitchNumber, setSelectedPitchNumber } = useChartBrokerStore();

  const flattenedPoints = useMemo(() => {
    return chartDataDisplay.flatMap((group) => group.points.map((point) => ({ ...point, groupLabel: group.label })));
  }, [chartDataDisplay]);

  const handleSelect = useCallback(
    (pitchOfGame: number) => {
      selectedPitchNumber === pitchOfGame ? setSelectedPitchNumber(undefined) : setSelectedPitchNumber(pitchOfGame);
    },
    [setSelectedPitchNumber]
  );

  return (
    <div className='PitchLocationChart-container' onClick={() => enablePitchSelection && setSelectedPitchNumber(undefined)}>
      <StrikeZone basePlateOrientation={basePlateOrientation} showBufferZone={showBufferZone} showStrikeZoneGrid={showStrikeZoneGrid}>
        {flattenedPoints.map((point, i) => (
          <PitchPoint
            key={`${point.groupLabel}-${point.pitchOfGame}-${i}`}
            point={point}
            orientation={basePlateOrientation}
            color={getGroupLabelColor(point.groupLabel)}
            textColor={getGroupLabelTextColor(point.groupLabel)}
            isSelected={selectedPitchNumber === point.pitchOfGame}
            onSelect={enablePitchSelection ? handleSelect : undefined}
            unitSystem={unitSystem}
          />
        ))}
      </StrikeZone>
      <Legend items={legendItems} />
    </div>
  );
};

export default PitchLocationChart;
